import UrlParse from 'url-parse';
import {findLanguage} from '@/locales';
import {sessionStorage, localStorage} from '@/utils/storage';
import windowUtils from '@/utils/window-utils';

const defaults = {
  mixpanel: {
    // mixpanel project: G2W Unified Client (Development)
    token: '0731f908b6b68dcce75e8da0509ae933'
  },
  brightcove: {
    accountId: '2368678545001', // Saas Dev on brightcove.com
    playerId: 'SJSFnPib-' // G2W Simulive player
  },
  wootric: {
    token: 'NPS-2d7cb989' // wootric account: g2whtml5wootrictrial@citrite.net
  },
  sentryio: {
    // see entries here: https://sentry.jmawsmonitor.net/sentry/g2w-unified-app-dev/
    dsn: 'https://9f10fd67e2bc4076a69daabb45a4bca5@sentry.jmawsmonitor.net/51',
    options: { debug: true, release: `g2w-client@${VERSION}` }
  },
  bgw: {
    chunksizeMs: 4000  // must match hls stream chunk size
  }
};

const ED = Object.assign({}, defaults, {
  name: 'ED',
  globalBaseUrl: 'https://globaled1.g2w.test.expertcity.com',
  loggingUrl: 'https://logginged1.col.test.expertcity.com',
  rtcgwAudioUrl: 'https://audiogw-rc.dev.goto-rtc.com',
  rtcgwVideoUrl: 'https://videogw-rc.dev.goto-rtc.com',
  featureServiceBaseUrl: 'https://apied-featureservices.devplatform-dev.expertcity.com',
  authServiceBaseUrl: 'https://authenticationed1.col.test.expertcity.com',
  authServiceClientId: 'c345ff50-4e08-4726-99a3-0bb84c8141d7',
  registrationServiceBaseUrl: 'https://apied-registrations.lmiinc.test.expertcity.com',
  goToStageApiBaseUrl: 'https://apied.gotostage.test.expertcity.com',
  tangramBaseUrl: 'https://messagebused.serversdev.getgo.com/events',
  settingsServiceUrl: 'https://apied.servicesdev.goto.com',
  launchdarklyClientId: '5c5cc8310a528c372a74c3ec',
  bgw: {
    baseUrl: 'https://broadcast-ed.dev.goto-rtc.com'
  },
  recaptcha: {
    sitekey: '6Ld4zrsUAAAAAHn_hR8CH3b2oVMRI6GDmgOcXAMC'
  }
});

const RC = Object.assign({}, defaults, {
  name: 'RC',
  globalBaseUrl: 'https://globalrc1.g2w.test.expertcity.com',
  loggingUrl: 'https://loggingrc1.col.test.expertcity.com',
  rtcgwAudioUrl: 'https://audiogw-rc.dev.goto-rtc.com',
  rtcgwVideoUrl: 'https://videogw-rc.dev.goto-rtc.com',
  featureServiceBaseUrl: 'https://apirc-featureservices.devplatform-dev.expertcity.com',
  authServiceBaseUrl: 'https://authenticationrc1.col.test.expertcity.com',
  authServiceClientId: '2e4b4c90-4afe-4b0a-82e1-cd89ff18df26',
  registrationServiceBaseUrl: 'https://apirc-registrations.lmiinc.test.expertcity.com',
  goToStageApiBaseUrl: 'https://apirc.gotostage.test.expertcity.com',
  tangramBaseUrl: 'https://messagebusrc.serversdev.getgo.com/events',
  launchdarklyClientId: '5c5cc8570894073758f7829a',
  settingsServiceUrl: 'https://apirc.servicesdev.goto.com',

  bgw: {
    baseUrl: 'https://broadcast-rc.dev.goto-rtc.com'
  },
  recaptcha: {
    sitekey: '6Ldy0LsUAAAAAIAZy8_XZvNk-pZIIr9oT47T1TSb'
  }
});

const LIVE = Object.assign({}, defaults, {
  name: 'LIVE',
  globalBaseUrl: 'https://global.gotowebinar.com',
  loggingUrl: 'https://logging.getgo.com',
  rtcgwAudioUrl: 'https://audiogw-g2w-live.goto-rtc.com',
  rtcgwVideoUrl: 'https://videogw-g2w-live.goto-rtc.com',
  featureServiceBaseUrl: 'https://api.services.gotomeeting.com',
  authServiceBaseUrl: 'https://authentication.logmeininc.com/oauth',
  authServiceClientId: '5817dfee-3ba8-436b-a3ea-cfa9411ff5ff',
  registrationServiceBaseUrl: 'https://api-registrations.logmeininc.com',
  goToStageApiBaseUrl: 'https://api.gotostage.com',
  tangramBaseUrl: 'https://messagebus.servers.getgo.com/events',
  settingsServiceUrl: 'https://api.services.goto.com',

  brightcove: {
    accountId: '2797134697001', // Saas Prod on brightcove.com
    playerId: 'rkqSqphZ-' // G2W Simulive player
  },
  mixpanel: {
    // mixpanel project: G2W Unified Client (Live)
    token: 'd501e36420eac7a8f9d7a322a0dfa3e4'
  },
  wootric: {
    token: 'NPS-cf2f2be5' // wootric account: g2whtml5wootricprod@citrite.net
  },
  sentryio: {
    // see entries here: https://sentry.jmawsmonitor.net/sentry/g2w-unified-app/
    dsn: 'https://012b6dd654d94850a6437bc24cfababb@sentry.jmawsmonitor.net/50',
    options: {}
  },
  launchdarklyClientId: '5c5cc8310a528c372a74c3ed',

  bgw: {
    baseUrl: 'https://broadcast-live.goto-rtc.com'
  },
  recaptcha: {
    sitekey: '6LdD0bsUAAAAADmNvj6fVs3s_s_SowrcbfObnbo9'
  }
});

// nota bene: inherits entries from defaults and LIVE
const STAGE = Object.assign({}, defaults, LIVE, {
  name: 'STAGE',
  globalBaseUrl: 'https://globalstage.gotowebinar.com',
  loggingUrl: 'https://loggingstage.getgo.com',
  rtcgwAudioUrl: 'https://audiogw-stage.goto-rtc.com',
  rtcgwVideoUrl: 'https://videogw-stage.goto-rtc.com',
  featureServiceBaseUrl: 'https://apistage.services.gotomeeting.com',
  authServiceBaseUrl: 'https://authenticationstage.logmeininc.com/oauth',
  registrationServiceBaseUrl: 'https://apistage-registrations.logmeininc.com',
  goToStageApiBaseUrl: 'https://apistage.gotostage.com',
  tangramBaseUrl: 'https://messagebusstage.servers.getgo.com/events',
  launchdarklyClientId: '5c5cc85e06e5d3372def855c',
  settingsServiceUrl: 'https://apistage.services.goto.com',

  bgw: {
    baseUrl: 'https://broadcast-stage.goto-rtc.com'
  },
  recaptcha: {
    sitekey: '6Lcs0bsUAAAAACn82MMW2SwfVTB65Duka7y7mLhQ'
  }
});

const all = {
  ED, RC, STAGE, LIVE
};

const isED = (host) => /ed1\./i.test(host);

const isRC = (host) => /rc1\./i.test(host);

const isSTAGE = (host) => /stage\./i.test(host);

const isLIVE = (host) => /(app|attendee|global|register)\.gotowebinar\.com/i.test(host);

const isUnofficial = (host) => !isED(host) && !isRC(host) && !isSTAGE(host) && !isLIVE(host);

export const buildEnv = (env, win = window) => {
  if (isUnofficial(win.location.host)) {
    // unofficial location (local etc)
    return Object.assign({}, env, {
      // don't send logs, telemetry, etc.
      loggingUrl: '',
      sentryio: {},
      mixpanel: {}
    });
  }

  return env;
};

const storageKey = 'g2w-env';

export const getFromHostname = (hostname, win = window) => {
  const host = hostname || win.location.host;
  let env = ED;

  if (isED(host)) {
    env = ED;
  }

  if (isRC(host)) {
    env = RC;
  }

  if (isSTAGE(host)) {
    env = STAGE;
  }

  if (isLIVE(host)) {
    env = LIVE;
  }

  return buildEnv(env, win);
};

export const getInitial = () => {
  const g2wEnv = sessionStorage.getItem(storageKey) || '';
  if (g2wEnv) {
    const env = all[g2wEnv.toUpperCase()];
    return buildEnv(env);
  }

  return getFromHostname();
};

const mutations = {
  setCurrent(state, payload) {
    if (!state.all[payload.name]) {
      throw new Error('invalid environment name', {name: payload.name});
    }
    state.current = buildEnv(state.all[payload.name]);
    if (state.current) {
      sessionStorage.setItem(storageKey, state.current.name);
    }
  },

  setFromHostName(state, payload) {
    state.current = getFromHostname(payload.hostname);
    if (state.current) {
      sessionStorage.setItem(storageKey, state.current.name);
    }
  },

  setProxyUrl(state, payload) {
    state.proxyUrl = payload.proxyUrl;
  },

  setLocale(state, payload) {
    state.locale = findLanguage(payload.locale);
  },

  setProduct(state, payload) {
    state.product = payload.product;
  }
};

const locationEnv = getFromHostname(window.location.host);

const getters = {
  globalUrl(state) {
    return (path = '') => {
      const globalBaseUrl = state.current.globalBaseUrl;
      let globalUrl;

      if (state.current !== locationEnv && state.proxyUrl) {
        globalUrl = new UrlParse(path, state.proxyUrl, true);
        globalUrl.query.target = globalBaseUrl;
      } else {
        globalUrl = new UrlParse(path, globalBaseUrl);
      }

      return globalUrl.toString();
    };
  },

  simuliveServiceUrl(state) {
    return `${state.current.featureServiceBaseUrl}/simulive`;
  },

  questionServiceBaseUrl(state) {
    return `${state.current.featureServiceBaseUrl}/qanda`;
  },

  registrationServiceBaseUrl(state) {
    return `${state.current.featureServiceBaseUrl}/registrationservice`;
  },

  pollingServiceBaseUrl(state) {
    return `${state.current.featureServiceBaseUrl}/pollingservice`;
  },

  eventServiceBaseUrl(state) {
    return `${state.current.featureServiceBaseUrl}/events`;
  },

  embeddedResourceServiceBaseUrl(state) {
    return `${state.current.featureServiceBaseUrl}/embeddedresources`;
  },

  settingsServiceUrl(state) {
    return `${state.current.settingsServiceUrl}/v1/settings`
  }
};

function uniqueValue() {
  return JSON.stringify(Math.random()).slice(2); // not as long as a real uuid, but good enough
}

function getMachineId() {
  return localStorage.getItem('machineId', uniqueValue(), true);
}

export function getBrowserLanguage() {
  const navigator = windowUtils.getGlobal('navigator');
  return (
    navigator.language
    || (navigator.languages && navigator.languages[0])
    || navigator.browserLanguage // IE
    || 'en'
  );
}

const state = () => ({
  all,
  current: getInitial(),
  proxyUrl: '',
  machineId: getMachineId(),
  uuid: uniqueValue(),
  locale: getBrowserLanguage(),
  product: 'GoTo Webinar'
});

export default {
  namespaced: true,
  state,
  mutations,
  getters
};
